import React, { useState } from "react"

import { Stack, Typography, useTheme } from "@mui/material"
import { useMutationPostHrCreateJobs } from "api/reactQuery/mutations/jobs"
import { ISuggestedCreateJobs } from "api/reactQuery/mutations/jobs.types"
import { useQueryGetHrJobs } from "api/reactQuery/queries/jobs"
import { useQueryStudentClassesById } from "api/reactQuery/queries/studentClasses"
import AddJobMenu from "components/common/addJobMenu/AddJobMenu"
import Dialog from "components/common/dialog/dialog"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import AddSuggestedJobs from "pages/jobs/addSuggestedJobs"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useParams } from "react-router"
import { getErrorMessageFromTab } from "utils/api"
import { renderMultipleComponents } from "utils/jobs/jobs"

import JobsSkeleton from "../../common/skeleton/jobs"
import Board from "./board"
import DialogAddJob from "./dialogAddJob"
import ListUsers from "./listUsers"
import NoJobs from "./noJobs"

const Hr = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const params = useParams()
  const queryClient = useQueryClient()
  const isTablet = useIsBreakpointUp("desktop")
  const theme = useTheme()
  const isMobile = useIsBreakpointUp("tablet")

  const [richEditorResponsibilities, setRichEditorResponsibilities] =
    useState("")
  const [richEditorQualifications, setRichEditorQualifications] = useState("")
  const [checked, setChecked] = useState<ISuggestedCreateJobs[]>([])
  const [errorSuggested, setErrorSuggested] = useState<string>("")
  const {
    isOpen: isOpenAddJob,
    handleOpen: handleOpenAddJob,
    handleClose: handleCloseAddJob,
  } = useDialog()
  const {
    isOpen: isOpenAddSuggestedJobs,
    handleOpen: handleOpenAddSuggestedJobs,
    handleClose: handleCloseAddSuggestedJobs,
  } = useDialog()
  const {
    isOpen: isDialogCloseSuggestedJobs,
    handleOpen: handleOpenDialogCloseSuggestedJobs,
    handleClose: handleCloseDialogCloseSuggestedJobs,
  } = useDialog()
  const { data: paymentClass } = useQueryStudentClassesById({
    id: Number(params.classId),
  })

  const { data: jobs, isLoading: loadingJobs } = useQueryGetHrJobs({
    classId: Number(params.classId),
  })

  const { mutate: createJob, isLoading: isLoadingCreateJob } =
    useMutationPostHrCreateJobs({
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries("jobsHr")
          showSnackbar({
            title: t("jobs.succeedCreateJob"),
            variant: "success",
          })
          handleCloseAddJob()
          handleCloseAddSuggestedJobs()
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessageFromTab(error),
            variant: "error",
          })
        },
      },
    })

  return (
    <Stack
      flexDirection="row"
      sx={{
        height: "calc(100vh - 150px)",
        overflow: "hidden"
      }}
    >
      {isTablet && (
        <Stack
          sx={{
            flex: "0 0 300px",
            overflowY: "auto",
            overflowX: "hidden",
            height: "100%",
            backgroundColor: theme.palette.common.white,
            border: `2px solid ${theme.palette.mockup.primary95}`,
          }}
        >
          <ListUsers />
        </Stack>
      )}
      <Stack sx={{ flex: "1", ml: isTablet ? 4 : 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <Stack
          sx={{ flexShrink: 0, mb: 2 }}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            flexDirection="row"
            alignItems="center"
            sx={{
              backgroundColor: isMobile ? theme.palette.common.white : "inset",
              p: 2,
            }}
          >
            <Typography
              component="h1"
              sx={{ fontSize: "26px" }}
              color="mockup.neutral10"
              flex="0 0 150px"
            >
              {t("jobs.jobBoard")}
            </Typography>
            {isMobile && (
              <Typography variant="subtitle2" fontWeight="500" flex="1 1 0">
                {t("jobs.assignedAsHrRepresentative")}
              </Typography>
            )}
          </Stack>
          <>
            <AddJobMenu
              buttonLabel={t("jobs.addJob")}
              onAddJob={handleOpenAddJob}
              onAddSuggestedJobs={handleOpenAddSuggestedJobs}
              addJobLabel={t("jobs.addJob")}
              addSuggestedLabel={t("jobs.addSuggestedJobs")}
            />
          </>
        </Stack>
        {loadingJobs ? (
          renderMultipleComponents(
            <JobsSkeleton
              height="200px"
              style={{
                margin: "10px 0 ",
              }}
            />,
            2
          )
        ) : (
          <Stack
            direction={jobs && jobs.data.length > 0 ? "row" : "column"}
            flexWrap="wrap"
            justifyContent="flex-start"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              height: 0,
            }}
          >
            {!jobs?.data.length ? (
              <NoJobs
                richEditorResponsibilities={richEditorResponsibilities}
                setRichEditorResponsibilities={setRichEditorResponsibilities}
                richEditorQualifications={richEditorQualifications}
                setRichEditorQualifications={setRichEditorQualifications}
              />
            ) : (
              jobs?.data
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((job) => <Board job={job} key={job.id} />)
            )}
          </Stack>
        )}
      </Stack>
      <DialogAddJob
        setRichEditorQualifications={setRichEditorQualifications}
        setRichEditorResponsibilities={setRichEditorResponsibilities}
        richEditorResponsibilities={richEditorResponsibilities}
        richEditorQualifications={richEditorQualifications}
        handleClose={handleCloseAddJob}
        isOpen={isOpenAddJob}
        isLoading={isLoadingCreateJob}
        onCreate={createJob}
      />
      <Dialog
        titleText={t("jobs.addSuggestedJobs")}
        open={isOpenAddSuggestedJobs}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            handleOpenDialogCloseSuggestedJobs()
          } else {
            handleCloseAddSuggestedJobs()
          }
        }}
        desktopMaxWidth="1040px"
        tabletMaxWidth="867px"
        actionAcceptText={t("jobs.addSelectedJobs", { count: checked.length })}
        onActionButtonClick={() => {
          if (checked.length === 0) {
            setErrorSuggested(t("jobs.atleastOneCheckbox"))
            return
          }
          if (
            !!checked.find(
              (el) => (el.title && el.title.length <= 3) || !el.title
            )
          ) {
            showSnackbar({
              title: t("jobs.selectedItemsShouldHaveTitleOfAtLeast3Characters"),
              variant: "error",
            })
            return
          }
          if (!!checked.find((el) => el.salary === 0)) {
            showSnackbar({
              title: t("jobs.amountOfSelectedItemsHaveToBePositiveNumber"),
              variant: "error",
            })
            return
          }
          createJob({
            classId: Number(params.classId),
            data: checked,
          })
        }}
      >
        <AddSuggestedJobs
          paymentClass={paymentClass?.data}
          handleSuggestedJobs={(el) => {
            const currentPosition = checked.findIndex((c) => c.id === el.id)
            const newChecked = [...checked]
            if (currentPosition === -1) {
              newChecked.push(el)
              el.check = true
            } else {
              newChecked.splice(currentPosition, 1)
              el.check = false
            }
            setChecked(newChecked)
            if (newChecked.length > 0) setErrorSuggested("")
          }}
          setChecked={setChecked}
          checked={checked}
          errorSuggested={errorSuggested}
          handleOpenDialogCloseSuggestedJobs={handleCloseAddSuggestedJobs}
        />
      </Dialog>
      <Dialog
        open={isDialogCloseSuggestedJobs}
        onClose={handleCloseDialogCloseSuggestedJobs}
        titleText={t("jobs.areYouSureYouWantToExit")}
        descriptionText={t("jobs.noChangesWillBeSaved")}
        actionAcceptText={t("jobs.accept")}
        onActionButtonClick={() => {
          handleCloseDialogCloseSuggestedJobs()
          handleCloseAddSuggestedJobs()
        }}
      />
    </Stack>
  )
}
export default Hr
